import React, { useEffect } from "react";

import "./WiredropDownload.scss";

const WIREDROP_WINDOWS_DOWNLOAD_URL =
	"https://github.com/eurycele/wiredrop-releases/releases/download/v2.3.0/WireDrop.Setup.msi";

const WIREDROP_ANDROID_DOWNLOAD_URL = "https://play.google.com/store/apps/details?id=com.eurycele.wiredrop";

const WIREDROP_MS_STORE_URL = "https://apps.microsoft.com/store/detail/9NN4S868G1LV?cid=DevShareMWAPCS";

const WIREDROP_LITE_WINDOWS_DOWNLOAD_URL =
	"https://github.com/eurycele/wiredroplite-releases/releases/latest/download/WireDropLite.zip";

const WIREDROP_UBUNTU_DEB_DOWNLOAD_URL =
	"https://github.com/eurycele/wiredrop-linux-releases/releases/download/v1.0.0/wiredrop_1.0.0_amd64.deb";

const features = [
	{
		icon: "fa-solid fa-comments",
		title: "Local Network Messaging",
		description: "Send instant messages to any device on your local network — no internet required."
	},
	{
		icon: "fa-solid fa-bolt",
		title: "Fast File Sharing",
		description: "Transfer files at full local network speed with no size limits or upload queues."
	},
	{
		icon: "fa-solid fa-folder-open",
		title: "Folder Transfer",
		description: "Share entire folders between devices in one click. Full folder support on desktop."
	},
	{
		icon: "fa-solid fa-cloud-arrow-up",
		title: "No Cloud Dependency",
		description: "Everything stays on your local network. No accounts, no cloud, no data leaves your premises."
	},
	{
		icon: "fa-solid fa-network-wired",
		title: "Device Discovery",
		description: "Automatically discovers all WireDrop devices on the same network — zero manual setup."
	},
	{
		icon: "fa-solid fa-layer-group",
		title: "Simple Interface",
		description: "Clean, minimal design that gets out of your way and lets you focus on sharing."
	}
];

const steps = [
	{
		number: "01",
		title: "Install WireDrop",
		description: "Download and run the MSI installer on your Windows device. Setup takes less than a minute."
	},
	{
		number: "02",
		title: "Connect to Network",
		description: "Ensure all your devices are on the same local network or Wi-Fi connection."
	},
	{
		number: "03",
		title: "Start Sharing",
		description: "Open WireDrop, discover nearby devices, and start messaging and transferring files instantly."
	}
];

const connectivityPairs = [
	{ from: "fa-brands fa-ubuntu", fromLabel: "Ubuntu", to: "fa-brands fa-windows", toLabel: "Windows" },
	{ from: "fa-brands fa-ubuntu", fromLabel: "Ubuntu", to: "fa-solid fa-bolt", toLabel: "WireDrop Lite" },
	{ from: "fa-brands fa-ubuntu", fromLabel: "Ubuntu", to: "fa-brands fa-android", toLabel: "Android" },
	{ from: "fa-brands fa-ubuntu", fromLabel: "Ubuntu", to: "fa-brands fa-ubuntu", toLabel: "Ubuntu" },
	{ from: "fa-brands fa-android", fromLabel: "Android", to: "fa-brands fa-windows", toLabel: "Windows" },
	{ from: "fa-brands fa-windows", fromLabel: "Windows", to: "fa-brands fa-windows", toLabel: "Windows" },
	{ from: "fa-solid fa-bolt", fromLabel: "WireDrop Lite", to: "fa-brands fa-android", toLabel: "Android" },
	{ from: "fa-solid fa-bolt", fromLabel: "WireDrop Lite", to: "fa-brands fa-windows", toLabel: "Windows" }
];

const ubuntuInstallSteps = [
	{
		number: "01",
		title: "Download the .deb package",
		body: (
			<>
				<p>
					Download WireDrop for Ubuntu from the{" "}
					<a
						className="ns-ubuntu-inline-link"
						href={WIREDROP_UBUNTU_DEB_DOWNLOAD_URL}
						rel="noopener noreferrer"
						target="_blank">
						WireDrop Linux releases page
					</a>
					, or run the following in your terminal:
				</p>
				<pre className="ns-code-block">
					<code>
						wget
						https://github.com/eurycele/wiredrop-linux-releases/releases/download/v1.0.0/wiredrop_1.0.0_amd64.deb
					</code>
				</pre>
			</>
		)
	},
	{
		number: "02",
		title: "Install WireDrop",
		body: (
			<>
				<p>From the directory where the package was downloaded, run:</p>
				<pre className="ns-code-block">
					<code>sudo apt install ./wiredrop_1.0.0_amd64.deb</code>
				</pre>
				<p>If the file is inside your Downloads folder:</p>
				<pre className="ns-code-block">
					<code>{"cd ~/Downloads\nsudo apt install ./wiredrop_1.0.0_amd64.deb"}</code>
				</pre>
			</>
		)
	},
	{
		number: "03",
		title: "Launch WireDrop",
		body: (
			<>
				<p>After installation, open WireDrop from the Ubuntu applications menu, or launch it from terminal:</p>
				<pre className="ns-code-block">
					<code>wiredrop</code>
				</pre>
			</>
		)
	},
	{
		number: "04",
		title: "Connect with other devices",
		body: (
			<>
				<p>
					Make sure your Ubuntu device and other WireDrop devices are connected to the same Wi-Fi or local network.
					WireDrop for Ubuntu can communicate with:
				</p>
				<ul className="ns-ubuntu-list">
					<li>
						<i aria-hidden="true" className="fa-solid fa-check" />
						WireDrop for Windows
					</li>
					<li>
						<i aria-hidden="true" className="fa-solid fa-check" />
						WireDrop Lite for Windows
					</li>
					<li>
						<i aria-hidden="true" className="fa-solid fa-check" />
						WireDrop Android app
					</li>
					<li>
						<i aria-hidden="true" className="fa-solid fa-check" />
						Other WireDrop Ubuntu/Linux devices
					</li>
				</ul>
			</>
		)
	}
];

const ubuntuManageSteps = [
	{
		number: "U1",
		title: "Uninstall WireDrop",
		body: (
			<>
				<p>To uninstall WireDrop:</p>
				<pre className="ns-code-block">
					<code>sudo apt remove wiredrop</code>
				</pre>
				<p>To remove unused dependencies:</p>
				<pre className="ns-code-block">
					<code>sudo apt autoremove</code>
				</pre>
				<p>To also remove local configuration files:</p>
				<pre className="ns-code-block">
					<code>{"sudo apt purge wiredrop\nsudo apt autoremove"}</code>
				</pre>
			</>
		)
	},
	{
		number: "U2",
		title: "Update WireDrop",
		body: (
			<>
				<p>
					To update WireDrop manually, download the latest .deb release from the WireDrop Linux releases page, then
					install it again:
				</p>
				<pre className="ns-code-block">
					<code>sudo apt install ./wiredrop_1.0.0_amd64.deb</code>
				</pre>
				<p>Replace the file name with the latest downloaded version if it changes.</p>
			</>
		)
	}
];

const WiredropDownloadPage = () => {
	useEffect(() => {
		const prevTitle = document.title;
		document.title = "Download WireDrop | Eurycele";

		const metaDesc = document.querySelector("meta[name='description']");
		const prevDesc = metaDesc?.getAttribute("content") ?? "";
		metaDesc?.setAttribute("content", "Download WireDrop, a secure LAN messaging and file-sharing app by Eurycele.");

		return () => {
			document.title = prevTitle;
			metaDesc?.setAttribute("content", prevDesc);
		};
	}, []);

	return (
		<div className="ns-page">
			<HeroSection />
			<PlatformSection />
			<WiredropLiteSection />
			<UbuntuInstallSection />
			<ConnectivitySection />
			<FeaturesSection />
			<HowItWorksSection />
			<SystemRequirementsSection />
			<TrustSection />
			<FinalCtaSection />
		</div>
	);
};

const HeroSection = () => (
	<div className="ns-hero">
		<div className="ns-hero-inner">
			<div className="ns-hero-content">
				<p className="ns-eyebrow">by Eurycele</p>
				<h1 className="ns-hero-title">WireDrop</h1>
				<p className="ns-hero-subtitle">Secure LAN messaging and file sharing for your devices.</p>
				<p className="ns-hero-desc">
					WireDrop is now available on Android, Windows, and Ubuntu/Linux. Install it from Google Play, the
					Microsoft Store, the new Debian package for Ubuntu, or use WireDrop Lite on Windows without installation.
				</p>
				<div className="ns-hero-actions">
					<a className="ns-btn-primary" href={WIREDROP_WINDOWS_DOWNLOAD_URL} download>
						<i className="fa-brands fa-windows" />
						Download for Windows
					</a>
					<a className="ns-btn-primary" href={WIREDROP_MS_STORE_URL} rel="noopener noreferrer" target="_blank">
						<i className="fa-brands fa-microsoft" />
						Microsoft Store
					</a>
					<a
						className="ns-btn-primary"
						href={WIREDROP_ANDROID_DOWNLOAD_URL}
						rel="noopener noreferrer"
						target="_blank">
						<i className="fa-brands fa-google-play" />
						Google Play
					</a>
					<a
						className="ns-btn-primary"
						href={WIREDROP_UBUNTU_DEB_DOWNLOAD_URL}
						rel="noopener noreferrer"
						target="_blank">
						<i className="fa-brands fa-ubuntu" />
						Download for Ubuntu
					</a>
					<a className="ns-btn-outline" href="#wd-how-it-works">
						Learn More
					</a>
				</div>
				<p className="ns-hero-version">
					Version 2.3.0 &nbsp;·&nbsp; Windows 10+ &nbsp;·&nbsp; Android &nbsp;·&nbsp; Ubuntu (.deb)
				</p>
			</div>

			<div aria-hidden="true" className="ns-hero-visual">
				<div className="ns-ring ns-ring-3" />
				<div className="ns-ring ns-ring-2" />
				<div className="ns-ring ns-ring-1" />
				<div className="ns-hero-logo-wrap">
					<img alt="WireDrop app icon" src="/assets/images/wiredrop-logo.png" />
				</div>
			</div>
		</div>
	</div>
);

const PlatformSection = () => (
	<div className="ns-platforms">
		<div className="ns-platforms-inner">
			<div className="ns-section-head">
				<p className="ns-eyebrow">Download</p>
				<h2>Choose Your Platform</h2>
				<p className="ns-section-sub">
					WireDrop is available for Windows, Android, and Ubuntu/Linux. Need a lighter option? Try WireDrop Lite —
					no installation required. macOS support is in progress.
				</p>
			</div>

			<div className="ns-platform-grid">
				<div className="ns-platform-card ns-platform-active">
					<div className="ns-platform-icon">
						<i className="fa-brands fa-windows" />
					</div>
					<div className="ns-platform-info">
						<h4>Windows</h4>
						<p>Recommended for Windows desktop installation. Supports Windows 10 and later.</p>
					</div>
					<div className="ns-platform-downloads">
						<a className="ns-btn-download" href={WIREDROP_WINDOWS_DOWNLOAD_URL} download>
							<i className="fa-solid fa-arrow-down" />
							Download MSI
						</a>
						<a className="ns-btn-download" href={WIREDROP_MS_STORE_URL} rel="noopener noreferrer" target="_blank">
							<i className="fa-brands fa-microsoft" />
							Microsoft Store
						</a>
					</div>
				</div>

				<div className="ns-platform-card ns-platform-active">
					<div className="ns-platform-icon">
						<i className="fa-brands fa-android" />
					</div>
					<div className="ns-platform-info">
						<h4>Android</h4>
						<p>Available on Google Play. Supports Android devices on your local network.</p>
					</div>
					<a
						className="ns-btn-download"
						href={WIREDROP_ANDROID_DOWNLOAD_URL}
						rel="noopener noreferrer"
						target="_blank">
						<i className="fa-brands fa-google-play" />
						Get on Google Play
					</a>
				</div>

				<div className="ns-platform-card ns-platform-active">
					<div className="ns-platform-icon">
						<i className="fa-brands fa-ubuntu" />
					</div>
					<div className="ns-platform-info">
						<h4>Ubuntu / Linux</h4>
						<p>
							Available now as a Debian package (.deb). Compatible with supported Ubuntu/Linux systems and works
							with existing WireDrop platforms.
						</p>
					</div>
					<div className="ns-platform-downloads">
						<a
							className="ns-btn-download"
							href={WIREDROP_UBUNTU_DEB_DOWNLOAD_URL}
							rel="noopener noreferrer"
							target="_blank">
							<i className="fa-solid fa-arrow-down" />
							Download .deb
						</a>
						<a className="ns-btn-download-secondary" href="#wd-ubuntu-install">
							<i className="fa-solid fa-terminal" />
							View install steps
						</a>
					</div>
				</div>

				<div className="ns-platform-card ns-platform-upcoming">
					<div className="ns-platform-icon">
						<i className="fa-brands fa-apple" />
					</div>
					<div className="ns-platform-info">
						<h4>macOS</h4>
						<p>macOS support is in progress. Watch this space.</p>
					</div>
					<span className="ns-badge-soon">Coming Soon</span>
				</div>
			</div>
		</div>
	</div>
);

const WiredropLiteSection = () => (
	<div className="ns-lite">
		<div className="ns-lite-inner">
			<div className="ns-lite-content">
				<div className="ns-lite-text">
					<p className="ns-eyebrow">Lightweight Edition</p>
					<h2 className="ns-lite-heading">WireDrop Lite for Windows</h2>
					<p className="ns-lite-desc">
						WireDrop Lite is a lightweight Windows version of WireDrop that runs without installation. It is less
						than 400 KB and can connect seamlessly with the WireDrop Android app, the full WireDrop Windows app,
						and other WireDrop Lite devices on the same local Wi-Fi network.
					</p>
					<ul className="ns-lite-features">
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							No installation required — run directly from the ZIP
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Less than 400 KB — truly portable
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Connects with the WireDrop Android app
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Connects with the full WireDrop Windows app
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Works with other WireDrop Lite devices on the same Wi-Fi
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Compatible with Windows 7 and later
						</li>
					</ul>
					<a className="ns-btn-primary" href={WIREDROP_LITE_WINDOWS_DOWNLOAD_URL} download>
						<i className="fa-solid fa-download" />
						Download WireDrop Lite
					</a>
				</div>
				<div aria-hidden="true" className="ns-lite-badge-wrap">
					<div className="ns-lite-badge-icon">
						<img alt="WireDrop Lite icon" src="/assets/images/wiredrop-lite-logo.png" />
					</div>
					<div className="ns-lite-stat">
						<span className="ns-lite-stat-value">&lt; 400 KB</span>
						<span className="ns-lite-stat-label">Portable download</span>
					</div>
					<div className="ns-lite-stat">
						<span className="ns-lite-stat-value">Zero</span>
						<span className="ns-lite-stat-label">Installation needed</span>
					</div>
				</div>
			</div>
		</div>
	</div>
);

type UbuntuStep = { number: string; title: string; body: React.ReactNode };

const UbuntuAccordionPanel = ({
	step,
	isOpen,
	onToggle
}: {
	step: UbuntuStep;
	isOpen: boolean;
	onToggle: () => void;
}) => (
	<div className={`ns-ubuntu-panel${isOpen ? " ns-ubuntu-panel-open" : ""}`}>
		<button aria-expanded={isOpen} className="ns-ubuntu-panel-header" onClick={onToggle} type="button">
			<span className="ns-ubuntu-panel-num">{step.number}</span>
			<span className="ns-ubuntu-panel-title">{step.title}</span>
			<i aria-hidden="true" className={`fa-solid fa-caret-${isOpen ? "up" : "down"} ns-ubuntu-panel-caret`} />
		</button>
		{isOpen && <div className="ns-ubuntu-panel-body">{step.body}</div>}
	</div>
);

const UbuntuInstallSection = () => {
	const [openInstallIndex, setOpenInstallIndex] = React.useState<number>(0);
	const [openManageIndex, setOpenManageIndex] = React.useState<number>(-1);

	const toggleInstall = (index: number) => () => setOpenInstallIndex(openInstallIndex === index ? -1 : index);
	const toggleManage = (index: number) => () => setOpenManageIndex(openManageIndex === index ? -1 : index);

	return (
		<div className="ns-ubuntu" id="wd-ubuntu-install" /* eslint-disable-line react/forbid-dom-props */>
			<div className="ns-ubuntu-inner">
				<div className="ns-section-head">
					<p className="ns-eyebrow">Ubuntu / Linux</p>
					<h2>Install WireDrop on Ubuntu</h2>
					<p className="ns-section-sub">
						WireDrop for Ubuntu is now available as a Debian package. Install it on supported Ubuntu/Linux systems
						and connect with nearby WireDrop devices across Windows, Android, and WireDrop Lite.
					</p>
				</div>

				<div className="ns-ubuntu-actions">
					<a
						className="ns-btn-primary"
						href={WIREDROP_UBUNTU_DEB_DOWNLOAD_URL}
						rel="noopener noreferrer"
						target="_blank">
						<i className="fa-brands fa-ubuntu" />
						Download for Ubuntu
					</a>
				</div>

				<div className="ns-ubuntu-group">
					<h3 className="ns-ubuntu-group-title">Step-by-step install</h3>
					<div className="ns-ubuntu-list-wrap">
						{ubuntuInstallSteps.map((step: UbuntuStep, i: number) => (
							<UbuntuAccordionPanel
								key={step.number}
								isOpen={openInstallIndex === i}
								onToggle={toggleInstall(i)}
								step={step}
							/>
						))}
					</div>
				</div>

				<div className="ns-ubuntu-group">
					<h3 className="ns-ubuntu-group-title">Uninstall &amp; update</h3>
					<div className="ns-ubuntu-list-wrap">
						{ubuntuManageSteps.map((step: UbuntuStep, i: number) => (
							<UbuntuAccordionPanel
								key={step.number}
								isOpen={openManageIndex === i}
								onToggle={toggleManage(i)}
								step={step}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

const ConnectivitySection = () => (
	<div className="ns-connectivity">
		<div className="ns-connectivity-inner">
			<div className="ns-section-head ns-section-head-light">
				<p className="ns-eyebrow">Local Network</p>
				<h2>Works across Android, Windows, WireDrop Lite, and Ubuntu</h2>
				<p className="ns-section-sub">
					WireDrop devices discover and communicate with each other over the same Wi-Fi or local network. The
					Ubuntu release works with WireDrop for Windows, WireDrop Lite for Windows, and the WireDrop Android app
					available on Google Play Store.
				</p>
			</div>
			<div className="ns-connectivity-grid">
				{connectivityPairs.map((pair: { from: string; fromLabel: string; to: string; toLabel: string }) => (
					<div key={`${pair.fromLabel}-${pair.toLabel}`} className="ns-connectivity-pair">
						<div className="ns-connectivity-node">
							<i className={pair.from} />
							<span>{pair.fromLabel}</span>
						</div>
						<i aria-hidden="true" className="fa-solid fa-arrows-left-right ns-conn-arrow" />
						<div className="ns-connectivity-node">
							<i className={pair.to} />
							<span>{pair.toLabel}</span>
						</div>
					</div>
				))}
			</div>
		</div>
	</div>
);

const FeaturesSection = () => (
	<div className="ns-features">
		<div className="ns-features-inner">
			<div className="ns-section-head ns-section-head-light">
				<p className="ns-eyebrow">Features</p>
				<h2>Why WireDrop?</h2>
				<p className="ns-section-sub">Everything you need for fast, private, device-to-device communication.</p>
			</div>

			<div className="ns-features-grid">
				{features.map((f: { icon: string; title: string; description: string }) => (
					<div key={f.title} className="ns-feature-card">
						<div className="ns-feature-icon">
							<i className={f.icon} />
						</div>
						<h4>{f.title}</h4>
						<p>{f.description}</p>
					</div>
				))}
			</div>
		</div>
	</div>
);

const HowItWorksSection = () => (
	<div className="ns-how" id="wd-how-it-works" /* eslint-disable-line react/forbid-dom-props */>
		<div className="ns-how-inner">
			<div className="ns-section-head">
				<p className="ns-eyebrow">Simple Setup</p>
				<h2>How It Works</h2>
				<p className="ns-section-sub">Get started in minutes — no accounts, no configuration needed.</p>
			</div>

			<div className="ns-steps">
				{steps.map((step: { number: string; title: string; description: string }, i: number) => (
					<React.Fragment key={step.number}>
						<div className="ns-step">
							<div className="ns-step-num">{step.number}</div>
							<div className="ns-step-body">
								<h4>{step.title}</h4>
								<p>{step.description}</p>
							</div>
						</div>
						{i < steps.length - 1 && <div aria-hidden="true" className="ns-step-line" />}
					</React.Fragment>
				))}
			</div>
		</div>
	</div>
);

const SystemRequirementsSection = () => (
	<div className="ns-reqs">
		<div className="ns-reqs-inner">
			<div className="ns-section-head">
				<p className="ns-eyebrow">Compatibility</p>
				<h2>System Requirements</h2>
			</div>

			<div className="ns-reqs-card">
				<div className="ns-req-block">
					<div className="ns-req-header">
						<i className="fa-brands fa-windows" />
						<h4>Windows</h4>
					</div>
					<ul className="ns-req-list">
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Windows 10 or later recommended
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Local network connection required
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Installer format: MSI
						</li>
					</ul>
				</div>

				<div aria-hidden="true" className="ns-req-divider" />

				<div className="ns-req-block">
					<div className="ns-req-header">
						<i className="fa-solid fa-bolt" />
						<h4>WireDrop Lite</h4>
					</div>
					<ul className="ns-req-list">
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Windows 7 or later — no installation required
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Local network connection required
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Less than 400 KB — portable ZIP
						</li>
					</ul>
				</div>

				<div aria-hidden="true" className="ns-req-divider" />

				<div className="ns-req-block">
					<div className="ns-req-header">
						<i className="fa-brands fa-ubuntu" />
						<h4>Ubuntu / Linux</h4>
					</div>
					<ul className="ns-req-list">
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Available now as a Debian package (.deb)
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Supported Ubuntu/Linux systems (amd64)
						</li>
						<li>
							<i aria-hidden="true" className="fa-solid fa-check" />
							Local network connection required
						</li>
					</ul>
				</div>

				<div aria-hidden="true" className="ns-req-divider" />

				<div className="ns-req-block">
					<div className="ns-req-header">
						<i className="fa-brands fa-apple" />
						<h4>macOS</h4>
					</div>
					<ul className="ns-req-list">
						<li>
							<i aria-hidden="true" className="fa-solid fa-hourglass-half" />
							In progress — coming soon
						</li>
					</ul>
				</div>

				<div className="ns-req-note" role="note">
					<i aria-hidden="true" className="fa-solid fa-circle-info" />
					<p>Some features may vary by platform. Additional platform support is under active development.</p>
				</div>
			</div>
		</div>
	</div>
);

const TrustSection = () => (
	<div className="ns-trust">
		<div className="ns-trust-inner">
			<div aria-hidden="true" className="ns-trust-icon">
				<i className="fa-solid fa-shield-halved" />
			</div>
			<div className="ns-trust-body">
				<h3>Built for private local communication</h3>
				<p>
					WireDrop is designed from the ground up for device-to-device communication over your local network. There
					are no accounts to create, no data sent to external servers, and no cloud involvement. Your messages and
					files travel only between your devices — keeping your data exactly where it belongs.
				</p>
			</div>
		</div>
	</div>
);

const FinalCtaSection = () => (
	<div className="ns-final-cta">
		<div className="ns-final-cta-inner">
			<h2>Ready to share faster?</h2>
			<p>Start messaging and transferring files across your local network today.</p>
			<div className="ns-final-cta-actions">
				<a className="ns-btn-primary ns-btn-lg" href={WIREDROP_WINDOWS_DOWNLOAD_URL} download>
					<i className="fa-brands fa-windows" />
					Download for Windows
				</a>
				<a
					className="ns-btn-primary ns-btn-lg"
					href={WIREDROP_MS_STORE_URL}
					rel="noopener noreferrer"
					target="_blank">
					<i className="fa-brands fa-microsoft" />
					Get from Microsoft Store
				</a>
				<a
					className="ns-btn-primary ns-btn-lg"
					href={WIREDROP_ANDROID_DOWNLOAD_URL}
					rel="noopener noreferrer"
					target="_blank">
					<i className="fa-brands fa-google-play" />
					Get on Google Play
				</a>
				<a
					className="ns-btn-primary ns-btn-lg"
					href={WIREDROP_UBUNTU_DEB_DOWNLOAD_URL}
					rel="noopener noreferrer"
					target="_blank">
					<i className="fa-brands fa-ubuntu" />
					Download for Ubuntu
				</a>
				<a className="ns-btn-primary ns-btn-lg" href={WIREDROP_LITE_WINDOWS_DOWNLOAD_URL} download>
					<i className="fa-solid fa-bolt" />
					Download WireDrop Lite
				</a>
			</div>
		</div>
	</div>
);

export default WiredropDownloadPage;
